import React, { useState, useEffect } from "react";
import { 
  Table, TableHeader, TableBody, TableRow, 
  TableHead, TableCell, TableCaption 
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CustomBadge } from "./utils/BadgeVariants";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { 
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { LayoutLoader } from "@/components/ui/layout-loader";
import { useToast } from "@/components/ui/use-toast";
import { 
  UserPlus, 
  Upload, 
  Download, 
  Tag, 
  Check, 
  FileText,
  Filter,
  Search,
  CreditCard,
  QrCode,
  Printer,
} from "lucide-react";
import { AngkasaMember, getAllMembers, saveMember, initializeAngkasaMemberDatabase } from "@/lib/db/angkasaMemberDb";

const AllCoopMembersTab = () => {
  const [members, setMembers] = useState<AngkasaMember[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<AngkasaMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [coopFilter, setCoopFilter] = useState("all");
  const [memberTypeFilter, setMemberTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const { toast } = useToast();

  const form = useForm({
    defaultValues: {
      memberId: "",
      name: "",
      icNumber: "",
      mobileNumber: "",
      email: "",
      memberType: "Primary",
      cooperativeName: "Retail Cooperative A",
      joinDate: new Date().toISOString().substring(0, 10),
      validUntil: new Date(new Date().setFullYear(new Date().getFullYear() + 3)).toISOString().substring(0, 10),
      promotionGroup: "Regular",
    },
  });

  useEffect(() => {
    const initializeDatabase = async () => {
      try {
        await initializeAngkasaMemberDatabase();
        fetchMembers();
      } catch (error) {
        console.error("Failed to initialize ANGKASA member database", error);
        toast({
          variant: "destructive",
          title: "Database Error",
          description: "Failed to initialize the ANGKASA member database.",
        });
        setIsLoading(false);
      }
    };

    initializeDatabase();
  }, [toast]);

  const fetchMembers = async () => {
    try {
      const data = await getAllMembers();
      setMembers(data);
      setFilteredMembers(data);
      setIsLoading(false);
    } catch (error) {
      console.error("Failed to fetch ANGKASA members", error);
      toast({
        variant: "destructive",
        title: "Fetch Error",
        description: "Failed to fetch ANGKASA members.",
      });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    filterMembers();
  }, [members, searchTerm, coopFilter, memberTypeFilter, statusFilter]);

  const filterMembers = () => {
    let filtered = [...members];

    // Apply search term filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(member => 
        member.memberId.toLowerCase().includes(searchLower) ||
        member.name.toLowerCase().includes(searchLower) ||
        member.icNumber.toLowerCase().includes(searchLower) ||
        member.email.toLowerCase().includes(searchLower)
      );
    }

    // Apply cooperative filter
    if (coopFilter && coopFilter !== "all") {
      filtered = filtered.filter(member => member.cooperativeName === coopFilter);
    }

    // Apply member type filter
    if (memberTypeFilter && memberTypeFilter !== "all") {
      filtered = filtered.filter(member => member.memberType === memberTypeFilter);
    }

    // Apply status filter (active/expired)
    if (statusFilter !== "all") {
      const today = new Date().toISOString().split('T')[0];
      if (statusFilter === "active") {
        filtered = filtered.filter(member => member.validUntil >= today);
      } else if (statusFilter === "expired") {
        filtered = filtered.filter(member => member.validUntil < today);
      }
    }

    setFilteredMembers(filtered);
  };

  const onSubmit = async (data: any) => {
    try {
      const newMember: AngkasaMember = {
        ...data,
        totalSpent: 0,
        lastPurchase: null
      };
      
      await saveMember(newMember);
      toast({
        title: "Member Added",
        description: `Successfully added ${newMember.name} to the system.`,
      });
      
      setIsAddMemberOpen(false);
      form.reset();
      fetchMembers();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add member.",
      });
    }
  };

  const handleBulkUpload = () => {
    toast({
      title: "Bulk Upload",
      description: "Bulk upload feature will be implemented soon.",
    });
  };

  const handleExport = () => {
    toast({
      title: "Export Members",
      description: "Export feature will be implemented soon.",
    });
  };

  const handlePrintCard = (member: AngkasaMember) => {
    toast({
      title: "Print Card",
      description: `Printing card for ${member.name}.`,
    });
  };

  const handleGenerateQR = (member: AngkasaMember) => {
    toast({
      title: "Generate QR",
      description: `Generating QR code for ${member.name}.`,
    });
  };

  const handleViewPurchaseHistory = (member: AngkasaMember) => {
    toast({
      title: "Purchase History",
      description: `Viewing purchase history for ${member.name}.`,
    });
  };

  const handleValidate = (member: AngkasaMember) => {
    toast({
      title: "Validation",
      description: `${member.name}'s identity validated successfully.`,
    });
  };

  const getMembershipStatus = (validUntil: string) => {
    const today = new Date().toISOString().split('T')[0];
    return validUntil >= today ? "Active" : "Expired";
  };

  if (isLoading) {
    return <LayoutLoader message="Loading ANGKASA members..." />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row justify-between gap-4 mb-4">
        <div className="flex flex-1 items-center gap-2">
          <Search className="h-5 w-5 text-muted-foreground" />
          <Input
            placeholder="Search by ID, name, IC or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-sm"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Dialog open={isAddMemberOpen} onOpenChange={setIsAddMemberOpen}>
            <DialogTrigger asChild>
              <Button><UserPlus className="mr-2 h-4 w-4" />Add Member</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>Add New Coop Member</DialogTitle>
                <DialogDescription>
                  Enter the details of the new cooperative member.
                </DialogDescription>
              </DialogHeader>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="memberId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Member ID</FormLabel>
                          <FormControl>
                            <Input placeholder="COOP-XXXXX" {...field} required />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Full Name" {...field} required />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="icNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>IC Number</FormLabel>
                          <FormControl>
                            <Input placeholder="XXXXXX-XX-XXXX" {...field} required />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="mobileNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Mobile Number</FormLabel>
                          <FormControl>
                            <Input placeholder="01X-XXXXXXX" {...field} required />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input placeholder="email@example.com" {...field} required />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="memberType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Member Type</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="Primary">Primary</SelectItem>
                              <SelectItem value="Secondary">Secondary</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="cooperativeName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Cooperative</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select cooperative" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="Retail Cooperative A">Retail Cooperative A</SelectItem>
                              <SelectItem value="School Cooperative B">School Cooperative B</SelectItem>
                              <SelectItem value="Agricultural Cooperative C">Agricultural Cooperative C</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="joinDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Join Date</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="validUntil"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Valid Until</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="promotionGroup"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Promotion Group</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select group" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="Regular">Regular</SelectItem>
                              <SelectItem value="Silver">Silver</SelectItem>
                              <SelectItem value="Gold">Gold</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <DialogFooter>
                    <Button type="submit">Save Member</Button>
                  </DialogFooter>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
          <Button variant="outline" onClick={handleBulkUpload}>
            <Upload className="mr-2 h-4 w-4" />Bulk Upload
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" />Export
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Filter:</span>
        
        <Select value={coopFilter} onValueChange={setCoopFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Cooperative" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Cooperatives</SelectItem>
            <SelectItem value="Retail Cooperative A">Retail Cooperative A</SelectItem>
            <SelectItem value="School Cooperative B">School Cooperative B</SelectItem>
            <SelectItem value="Agricultural Cooperative C">Agricultural Cooperative C</SelectItem>
          </SelectContent>
        </Select>

        <Select value={memberTypeFilter} onValueChange={setMemberTypeFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Member Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="Primary">Primary</SelectItem>
            <SelectItem value="Secondary">Secondary</SelectItem>
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableCaption>A list of all cooperative members</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>Member ID</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>IC Number</TableHead>
              <TableHead>Mobile</TableHead>
              <TableHead>Member Type</TableHead>
              <TableHead>Cooperative</TableHead>
              <TableHead>Join Date</TableHead>
              <TableHead>Valid Until</TableHead>
              <TableHead>Total Spent (RM)</TableHead>
              <TableHead>Promo Group</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredMembers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} className="text-center py-4">No members found</TableCell>
              </TableRow>
            ) : (
              filteredMembers.map((member) => (
                <TableRow key={member.memberId}>
                  <TableCell className="font-medium">{member.memberId}</TableCell>
                  <TableCell>{member.name}</TableCell>
                  <TableCell>{member.icNumber}</TableCell>
                  <TableCell>{member.mobileNumber}</TableCell>
                  <TableCell>{member.memberType}</TableCell>
                  <TableCell>{member.cooperativeName}</TableCell>
                  <TableCell>{member.joinDate}</TableCell>
                  <TableCell>{member.validUntil}</TableCell>
                  <TableCell>{member.totalSpent.toFixed(2)}</TableCell>
                  <TableCell>
                    <Badge variant={
                      member.promotionGroup === "Gold" ? "default" : 
                      member.promotionGroup === "Silver" ? "secondary" : "outline"
                    }>
                      {member.promotionGroup}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <CustomBadge variant={getMembershipStatus(member.validUntil) === "Active" ? "success" : "destructive"}>
                      {getMembershipStatus(member.validUntil)}
                    </CustomBadge>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-1">
                      <Button variant="ghost" size="icon" onClick={() => handleViewPurchaseHistory(member)} title="View Purchase History">
                        <FileText className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleValidate(member)} title="Validate Identity">
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handlePrintCard(member)} title="Print Card">
                        <CreditCard className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleGenerateQR(member)} title="Generate QR Code">
                        <QrCode className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default AllCoopMembersTab;
